/**
 * Clientes parecidos al nombre escrito — para no crear un duplicado sin darse
 * cuenta.
 *
 * Lo pidió el propietario tras probar Oportunidades en staging (2026-10-25):
 * escribió «2018», guardó, y se creó un cliente nuevo llamado «2018» en vez de
 * ofrecerle el «2018 Apoyo, S.L.» que ya tenía. La lista nativa del navegador
 * apenas se ve y solo busca por el principio, con tildes y mayúsculas.
 *
 * Aquí se compara sin tildes, sin mayúsculas y con los espacios juntos:
 *
 * ```text
 * igual       "marta ruiz" es la "Marta Ruíz" que ya existe: no se crea otra
 * parecido    uno contiene al otro ("2018" y "2018 Apoyo, S.L."), o comparten
 *             una palabra de 4 letras o mas ("Reformas Ana" y "Ana Reformas")
 * ```
 *
 * No decide nada: la pantalla enseña los parecidos y, si aun asi el usuario
 * quiere uno nuevo, lo confirma.
 */

/** "  Instalación   ELÉCTRICA " -> "instalacion electrica". */
export function normalizarNombre(nombre: string): string {
  return nombre
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function palabras(nombreNormalizado: string): readonly string[] {
  return nombreNormalizado.split(/[^a-z0-9ñ]+/).filter((p) => p.length >= 4);
}

export function mismoCliente(escrito: string, nombre: string): boolean {
  const n = normalizarNombre(escrito);
  return n !== "" && n === normalizarNombre(nombre);
}

/**
 * Hasta `max` clientes que se parecen a `escrito`, sin el que es igual. Primero
 * los que EMPIEZAN por lo escrito, después el resto, cada grupo por nombre.
 */
export function clientesParecidos<C extends { readonly displayName: string }>(escrito: string, clientes: readonly C[], max = 5): C[] {
  const n = normalizarNombre(escrito);
  if (n.length < 2) return [];
  const suyas = palabras(n);
  const encontrados = clientes
    .map((c) => ({ c, nombre: normalizarNombre(c.displayName) }))
    .filter(({ nombre }) => {
      if (nombre === n) return false;
      if (nombre.includes(n)) return true;
      if (nombre.length >= 3 && n.includes(nombre)) return true;
      return palabras(nombre).some((p) => suyas.includes(p));
    });
  encontrados.sort((a, b) => {
    const pa = a.nombre.startsWith(n) ? 0 : 1;
    const pb = b.nombre.startsWith(n) ? 0 : 1;
    return pa !== pb ? pa - pb : a.nombre.localeCompare(b.nombre, "es");
  });
  return encontrados.slice(0, max).map(({ c }) => c);
}

/**
 * La búsqueda del tablero (`OD-0048-ENMIENDA-02` F-03), con la misma regla:
 * sin tildes ni mayúsculas, y basta con que uno de los textos contenga lo
 * escrito. Vacío, todo coincide.
 */
export function coincideBusqueda(escrito: string, ...textos: ReadonlyArray<string | null>): boolean {
  const n = normalizarNombre(escrito);
  if (n === "") return true;
  return textos.some((x) => x !== null && normalizarNombre(x).includes(n));
}
